import type { ReactNode } from "react";
import React, { createContext, useCallback, useContext, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import ApiHttp from "../ApiHttp";
import { useLibrary } from "../dbbl/providers/useLibrary.hook";

import { Role } from "./Role";

type OrgUuid = UUID | Brand<string, "OrganizationId"> | null | undefined;

// Role detail queries are keyed by org so they can be invalidated together.
const roleDetailKey = (orgUuid: OrgUuid) => ["roleDetail", orgUuid] as const;

/**
 * Create body. `account_permissions` is the full per-account configuration (access and
 * account-level permissions). Omit it to save only the flat `permissions`.
 * `accounts_opened_later` is only accepted alongside `account_permissions` (or on a converted role).
 */
export type RoleCreatePayload = ReturnType<Role["serialize"]> & {
  account_permissions?: API.AccountPermission[];
  accounts_opened_later?: API.AccountsOpenedLater;
};

/** Like `RoleCreatePayload`, for the role to update. Omit either scoped field to leave it as is. */
export type RoleUpdatePayload = RoleCreatePayload & { uuid: string };

type RoleDetailQuery = {
  queryKey: readonly unknown[];
  queryFn: () => Promise<Role>;
  enabled: boolean;
};

type RolesContextType = {
  orgUuid: OrgUuid;
  isLoading: boolean;
  roles: Role[];
  assignableRoles: Role[];
  error?: Error;
  refresh: () => void;
  createRole: (role: RoleCreatePayload) => Promise<Role>;
  updateRole: (role: RoleUpdatePayload) => Promise<Role>;
  deleteRole: (uuid: string) => Promise<void>;
  /** The one query definition for a role's detail, shared by `useRoleDetail` and `fetchRoleDetail`. */
  roleDetailQuery: (roleUuid: string | null | undefined) => RoleDetailQuery;
  /**
   * Fetches a role's detail on demand, e.g. from an event handler, and resolves with it (from the
   * cache when it is still fresh). Use `useRoleDetail` when the detail is needed to render.
   */
  fetchRoleDetail: (roleUuid: string) => Promise<Role>;
};

const rolesContext = createContext<RolesContextType>({
  orgUuid: undefined,
  isLoading: false,
  roles: [],
  assignableRoles: [],
  error: undefined,
  refresh: () => {},
  createRole: () => Promise.resolve(new Role({ name: "" })),
  updateRole: () => Promise.resolve(new Role({ name: "", uuid: "required" })),
  deleteRole: () => Promise.resolve(),
  roleDetailQuery: () => ({
    queryKey: [],
    queryFn: () => Promise.resolve(new Role({ name: "" })),
    enabled: false,
  }),
  fetchRoleDetail: () => Promise.resolve(new Role({ name: "" })),
});

export const useRolesContext = () => useContext(rolesContext);

/**
Fetches a single role with its per-account permissions
(`GET /organizations/{orgUuid}/roles/{roleUuid}`). The list from `useRolesContext` stays lean, so
use this on demand wherever per-account data is needed. To fetch from an event handler instead,
use `fetchRoleDetail` from `useRolesContext`; both share one query and cache entry. Must be used
under `RolesProvider`.
* */
export const useRoleDetail = (roleUuid: string | null | undefined) => {
  const { roleDetailQuery } = useRolesContext();
  return useQuery(roleDetailQuery(roleUuid));
};

export const RolesProvider = ({
  orgUuid,
  children,
}: {
  // Nullable so consumers can mount the provider unconditionally: swapping it
  // in and out as an org appears remounts everything below it.
  orgUuid: OrgUuid;
  children: ReactNode;
}) => {
  const [lastUpdateRequested, setLastUpdateRequested] = React.useState<Date>(
    new Date(),
  );
  const { token, apiUrl } = useLibrary("network");
  const { isLoading, data, error } = useQuery({
    queryKey: ["roles", orgUuid, lastUpdateRequested.getTime(), token, apiUrl],
    queryFn: () =>
      ApiHttp.fetch<{ roles: API.Role[] }>(
        `${apiUrl}/organizations/${orgUuid}/roles`,
        {
          token,
          endpointIsUrl: true,
        },
      ).then((responseData) =>
        responseData.roles.map((r: API.Role) => Role.deserialize(r)),
      ),
    enabled: !!orgUuid && !!token,
  });
  const queryClient = useQueryClient();
  const roleDetailQuery = useCallback(
    (roleUuid: string | null | undefined): RoleDetailQuery => ({
      queryKey: [...roleDetailKey(orgUuid), roleUuid, token, apiUrl],
      queryFn: () =>
        ApiHttp.fetch<API.RoleDetail>(
          `${apiUrl}/organizations/${orgUuid}/roles/${roleUuid}`,
          { token, endpointIsUrl: true },
        ).then((responseData) => Role.deserialize(responseData)),
      enabled: !!orgUuid && !!roleUuid && !!token,
    }),
    [orgUuid, token, apiUrl],
  );
  const fetchRoleDetail = useCallback(
    (roleUuid: string) => queryClient.fetchQuery(roleDetailQuery(roleUuid)),
    [queryClient, roleDetailQuery],
  );
  const refresh = useCallback(() => setLastUpdateRequested(new Date()), []);
  const refreshAfterSave = useCallback(() => {
    refresh();
    queryClient.invalidateQueries({ queryKey: roleDetailKey(orgUuid) });
  }, [refresh, queryClient, orgUuid]);
  const createMutation = useMutation({
    mutationFn: (role: RoleCreatePayload) =>
      ApiHttp.fetch<API.Role>(
        `${apiUrl}/organizations/${orgUuid}/roles`,
        { method: "POST", token, endpointIsUrl: true },
        role,
      ).then((responseData) => Role.deserialize(responseData)),
    onSuccess: refreshAfterSave,
  });
  const updateMutation = useMutation({
    mutationFn: (role: RoleUpdatePayload) =>
      ApiHttp.fetch<API.Role>(
        `${apiUrl}/organizations/${orgUuid}/roles/${role.uuid}`,
        { method: "PATCH", token, endpointIsUrl: true },
        role,
      ).then((responseData) => Role.deserialize(responseData)),
    onSuccess: refreshAfterSave,
  });
  const deleteMutation = useMutation({
    mutationFn: (uuid: string) =>
      ApiHttp.fetch<void>(`${apiUrl}/organizations/${orgUuid}/roles/${uuid}`, {
        method: "DELETE",
        token,
        endpointIsUrl: true,
      }),
    onSuccess: (_data, uuid) => {
      refresh();
      // The role is gone, so refetching its detail would 404.
      queryClient.removeQueries({
        queryKey: [...roleDetailKey(orgUuid), uuid],
      });
    },
  });
  const value = useMemo(() => {
    const allRoles = data || [];
    return {
      orgUuid,
      isLoading,
      roles: allRoles,
      assignableRoles: allRoles.filter((role) => role.isAssignable()),
      error: error || undefined,
      refresh,
      createRole: createMutation.mutateAsync,
      updateRole: updateMutation.mutateAsync,
      deleteRole: deleteMutation.mutateAsync,
      roleDetailQuery,
      fetchRoleDetail,
    };
  }, [
    orgUuid,
    isLoading,
    data,
    error,
    refresh,
    createMutation.mutateAsync,
    updateMutation.mutateAsync,
    deleteMutation.mutateAsync,
    roleDetailQuery,
    fetchRoleDetail,
  ]);
  return (
    <rolesContext.Provider value={value}>{children}</rolesContext.Provider>
  );
};
